import { call, take, takeLatest, takeEvery, race, select } from "redux-saga/effects";
import { actionWatcher } from "services/redux/sagas/common/";
import * as links from "services/redux/sagas/links";
import { liveRefreshFor } from "../common";

import {
  loadSitesOnTestCountStart,
  loadSitesOnTestCountSuccess,
  loadSitesOnTestCountFailed,
  loadSitesOnTestCountCancel,
  loadSitesOnTestStart,
  loadSitesOnTestSuccess,
  loadSitesOnTestFailed,
  loadSitesOnTestCancel,
  loadSitesOnTestCountRefreshStart,
  loadSitesOnTestCountRefreshSuccess,
  loadSitesOnTestCountRefreshFailed,
  loadSitesOnTestRefreshStart,
  loadSitesOnTestRefreshSuccess,
  loadSitesOnTestRefreshFailed,
} from "services/redux/actions/sitesOnTest";

import {
  getSitesOnTestSortField,
  getSitesOnTestSortDirection,
  getSitesOnTestFilters,
  getSitesOnTestPageSize,
  getSitesOnTestRequestPageNumber,
} from "services/redux/selectors/sitesOnTest";
  
import conxtdOut from "apis/conxtdOut";

// sites on test count
export function* watchLoadSitesOnTestCount() {
  yield actionWatcher({
    actions: {
      start: loadSitesOnTestCountStart,
      failed: loadSitesOnTestCountFailed,
      success: loadSitesOnTestCountSuccess,
      cancel: loadSitesOnTestCountCancel,
    },
    saga: loadSitesOnTestCountRe,
  });
}

// /EnterpriseSites/sitesOnTestCount/{{enterprise_id}}/{{group_id}}
export function* loadSitesOnTestCountRe(action) {
  try {
    const { enterprise_id, group_id } = action;

    if (!enterprise_id || !group_id) 
      throw new Error(
        "group_id and enterprise_id are required for loading sites on test count"
      );

    const url = `/EnterpriseSites/sitesOnTestCount/${enterprise_id}/${group_id}`;

    const result = yield call(conxtdOut.get, url, action.params);

    return result;
  } catch (e) {
    // throw new Error(e);
    console.error(e);
  }
}

// sites on test count live refresh
export function* watchLoadSitesOnTestCountLiveRefresh() {
  yield takeEvery("LOAD_SITES_ON_TEST_COUNT_REFRESH_START", function*(action) {
    yield race({
      task: call(loadSitesOnTestCountLiveRefreshRe, action),
      cancel: take([
        "LOAD_SITES_ON_TEST_COUNT_REFRESH_CANCEL",
      ])
    });
  });
}

// /EnterpriseSites/sitesOnTestCount/{{enterprise_id}}/{{group_id}}
export function* loadSitesOnTestCountLiveRefreshRe(action) {
  try {
    const { enterprise_id, group_id } = action;

    if (!enterprise_id || !group_id) 
      throw new Error(
        "group_id and enterprise_id are required for loading sites on test count"
      );

    const interval = 30 * 1000; // refresh every 30 sec
    
    const params = {
      startAction: loadSitesOnTestCountRefreshStart,
      startActionParams: {
        ...action,
        interval,
      },
      successAction: loadSitesOnTestCountRefreshSuccess,
      successActionParams: {
        enterprise_id,
        group_id,
        interval,
      },
      failedAction: loadSitesOnTestCountRefreshFailed,
      apiCall: call(
        conxtdOut.get,
        `/EnterpriseSites/sitesOnTestCount/${enterprise_id}/${group_id}`
      ),
      interval,
      key: "data",
      refresh: true,
    };

    // const result = yield call(conxtdOut.get, url, action.params);
    const result = yield liveRefreshFor(params);

    return result;
  } catch (e) {
    // throw new Error(e);
    console.error(e);
  }
}

// sites on test
export function* watchLoadSitesOnTest() {
  // Link alarms loading to event meta
  yield takeLatest(
    ["LOAD_SITES_ON_TEST_REFRESH_START"],
    links.toEventMetaBackgroundLoader
  );

  // Link sitesOnTest loading to site data
  yield takeLatest(
    ["LOAD_SITES_ON_TEST_START"],
    links.toSitesBackgroundLoader
  );

  yield actionWatcher({
    actions: {
      start: loadSitesOnTestStart,
      failed: loadSitesOnTestFailed,
      success: loadSitesOnTestSuccess,
      cancel: loadSitesOnTestCancel,
    },
    saga: loadSitesOnTestRe,
  });
}

// /EnterpriseSites/sitesOnTest/{{enterprise_id}}/{{group_id}}
export function* loadSitesOnTestRe(action) {
  try {
    const { enterprise_id, group_id } = action;

    if (!enterprise_id || !group_id) 
      throw new Error(
        "group_id and enterprise_id are required for loading sites on test"
      );

    const limit = action.pageSize || 20;
    const page = action.requestPageNumber || 1;

    const sort = action.sortField || "";
    const direction = action.sortDirection || "";

    let filterSearchParams = [];
    if (action.filters && action.filters.length > 0) {
      action.filters.forEach(filter => {
        filterSearchParams[filter.id] = filter.value;
      });
    }

    const params = new URLSearchParams({
      limit: limit,
      page: page,
      sort: sort,
      direction: direction,
      ...filterSearchParams,
    });

    const url = `/EnterpriseSites/sitesOnTest/${enterprise_id}/${group_id}/?${params.toString()}`;

    const result = yield call(conxtdOut.get, url);

    return result;
  } catch (e) {
    // throw new Error(e);
    console.error(e);
  }
}

// sites on test live refresh
export function* watchLoadSitesOnTestLiveRefresh() {
  // Link alarms loading to event meta
  yield takeLatest(
    ["LOAD_SITES_ON_TEST_REFRESH_START"],
    links.toEventMetaBackgroundLoader
  );

  // Link sitesOnTest loading to site data
  yield takeLatest(
    ["LOAD_SITES_ON_TEST_REFRESH_START"],
    links.toSitesBackgroundLoader
  );

  yield takeEvery("LOAD_SITES_ON_TEST_REFRESH_START", function*(action) {
    yield race({
      task: call(loadSitesOnTestLiveRefreshRe, action),
      cancel: take([
        "LOAD_SITES_ON_TEST_REFRESH_CANCEL",
      ])
    });
  });
}

// /EnterpriseSites/sitesOnTest/{{enterprise_id}}/{{group_id}}
export function* loadSitesOnTestLiveRefreshRe(action) {
  try {
    const { enterprise_id, group_id } = action;

    if (!enterprise_id || !group_id) 
      throw new Error(
        "group_id and enterprise_id are required for loading sites on test"
      );

    // for live refresh, use the stored request parameters. there will be no changes to the request parameters coming in on the action
    const limit = yield select(getSitesOnTestPageSize) || 20;
    const page = yield select(getSitesOnTestRequestPageNumber) || 1;

    const sortField = yield select(getSitesOnTestSortField);
    const sortDirection = yield select(getSitesOnTestSortDirection);
    const filters = yield select(getSitesOnTestFilters);

    const sort = sortField || "";
    const direction = sortDirection || "";

    let filterSearchParams = [];
    if (filters && filters.length > 0) {
      filters.forEach(filter => {
        filterSearchParams[filter.id] = filter.value;
      });
    }

    const searchParams = new URLSearchParams({
      limit: limit,
      page: page,
      sort: sort,
      direction: direction,
      ...filterSearchParams,
    });

    const interval = 30 * 1000; // refresh every 30 sec
    
    const params = {
      startAction: loadSitesOnTestRefreshStart,
      startActionParams: {
        enterprise_id,
        group_id,
        sortField,
        sortDirection,
        filters,
      },
      successAction: loadSitesOnTestRefreshSuccess,
      successActionParams: {
        enterprise_id,
        group_id,
        sortField,
        sortDirection,
        filters,
      },
      failedAction: loadSitesOnTestRefreshFailed,
      apiCall: call(
        conxtdOut.get,
        `/EnterpriseSites/sitesOnTest/${enterprise_id}/${group_id}/?${searchParams.toString()}`
      ),
      interval,
      key: "data",
      refresh: true,
    };

    const result = yield liveRefreshFor(params);

    return result;
  } catch (e) {
    // throw new Error(e);
    console.error(e);
  }
}